/**
 * EmptyState - Centered message for when there's no content to display.
 * Use for empty lists, no search results, etc.
 */

import React from 'react';
import { View } from 'react-native';
import { Text } from '@gluestack-ui/themed';
import { FS_MD, FS_SM } from '../../theme';
import { TextLinkButton } from './TextLinkButton';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface EmptyStateProps {
  /** Primary message describing the empty state */
  message: string;
  /** Optional secondary/hint message */
  hint?: string;
  /** Optional action button */
  action?: {
    label: string;
    onPress: () => void;
  };
  testID?: string;
}

export const EmptyState = React.memo(function EmptyState({
  message,
  hint,
  action,
  testID,
}: EmptyStateProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();

  return (
    <View
      testID={testID}
      style={{ paddingVertical: 40, alignItems: 'center', paddingHorizontal: 20 }}
    >
      <Text
        allowFontScaling={false}
        style={{
          fontSize: FS_MD * fontScale,
          color: theme.textSecondary,
          textAlign: 'center',
          ...ffs,
        }}
      >
        {message}
      </Text>
      {hint ? (
        <Text
          allowFontScaling={false}
          style={{
            fontSize: FS_SM * fontScale,
            color: theme.textSecondary,
            textAlign: 'center',
            marginTop: 8,
            ...ffs,
          }}
        >
          {hint}
        </Text>
      ) : null}
      {action ? (
        <View style={{ marginTop: 16 }}>
          <TextLinkButton
            testID={testID ? `${testID}-action` : 'empty-action'}
            accessibilityRole="button"
            label={action.label}
            onPress={action.onPress}
          />
        </View>
      ) : null}
    </View>
  );
});
